import React, {useEffect, useRef} from 'react';
import {Button} from '@consta/uikit/Button';
import {IconClose} from '@consta/uikit/IconClose';
import {IconCheck} from '@consta/uikit/IconCheck';
import {IconShuffle} from '@consta/uikit/IconShuffle';
import {IconAdd} from '@consta/uikit/IconAdd';
import {NumberInput} from '../NumberInput';
import {BetInSchema, BetSchema, MatchWithBetSchema, UserScoreSchema} from '../../api';
import {displayMatchStatus, formatDateTime} from '../../utils/helpers';
import {Router} from '../../router';

interface MatchCardProps {
    match: MatchWithBetSchema;
    place?: UserScoreSchema;
    editingBet?: BetInSchema;
    betLoading: boolean;
    onEdit: (bet: BetInSchema) => void;
    onCancel: () => void;
    onSave: (matchId: number, bet?: BetSchema) => void;
    showDetails?: boolean;
}

export const MatchCard: React.FC<MatchCardProps> = ({
    match,
    place,
    editingBet,
    betLoading,
    onEdit,
    onCancel,
    onSave,
    showDetails=true,
}) => {
    const isEditing = editingBet?.match_id === match.id;
    const isCup = match.round?.type === 'cup';
    const canEdit = match.status === 'NOT_STARTED' && new Date(match.scheduled_at).getTime() > Date.now();
    const bet = match.bet;
    const allinMaxRef = useRef<number | null>(null);

    useEffect(() => {
        allinMaxRef.current = null;
    }, [isEditing]);

    const editBet = (overrides: Partial<BetInSchema>) => {
        if (!editingBet) return;
        onEdit({ ...editingBet, ...overrides });
    };

    return (
        <div key={match.id} className='match'>
            <h2 className='match__header'>{match.round_name}{match.round?.group && `, ${match.round.group}`}</h2>
            <div className='match__schedule'>
                <span className='match__schedule__text'>{formatDateTime(match.scheduled_at)}</span>
                <span className={`match__status match__status--${match.status.toLowerCase()}`}>
                    {displayMatchStatus(match.status)}
                    {match.current_minute && match.status === 'LIVE' && `, ${match.current_minute}'`}
                </span>
                {
                    showDetails ?
                    <a className='match__detail-link' href={Router.MATCH.replace(':matchId', match.id.toString())}>
                        Детали матча →
                    </a> :
                    <a className='match__detail-link' href={match.link}>Подробнее →</a>
                }
            </div>

            <div className='match__team match__team_home'>
                <div className='flag flag_small'>
                    <img className='flag__image' src={match.home?.team.logo} alt={match.home?.team.name} />
                </div>
                <h3 className='match__team__header'>{match.home?.team.name}</h3>
            </div>

            <div className='match__team match__team_away'>
                <div className='flag flag_small'>
                    <img className='flag__image' src={match.away?.team.logo} alt={match.away?.team.name} />
                </div>
                <h3 className='match__team__header'>{match.away?.team.name}</h3>
            </div>

            {/* Реальный счёт */}
            {match.status !== 'NOT_STARTED' && (
                match.home?.overtime_score !== null && match.away?.overtime_score !== null ? (
                    <div className='match__score-wrapper'>
                        <span className='match__score-label'>Основное: {match.home?.score} - {match.away?.score}</span>
                        <div className='match__score'>
                            <span>{match.home?.overtime_score}</span><span>-</span><span>{match.away?.overtime_score}</span>
                        </div>
                        <span className='match__score-label' style={{ marginTop: 4 }}>Доп. время</span>
                        {match.home?.penalty_score !== null && match.away?.penalty_score !== null && (
                            <span className='match__penalty_score'>
                                Пенальти: {match.home?.penalty_score} - {match.away?.penalty_score}
                            </span>
                        )}
                    </div>
                ) : (
                    <div className='match__score-wrapper'>
                        <span className='match__score-label'>Счёт</span>
                        <div className='match__score'>
                            <span>{match.home?.score}</span><span>-</span><span>{match.away?.score}</span>
                        </div>
                    </div>
                )
            )}
            {/* Ставка */}
            <div className='match__bet'>
                {/* РЕДАКТИРОВАНИЕ */}
                {isEditing && editingBet && (
                    <>
                        <div className='match__bet__fieldset'>
                            <h4 className='match__bet__label'>Основной счёт:</h4>
                            <div className='match__bet__fields'>
                                <NumberInput min={0} max={20} value={editingBet.home ?? 0}
                                    onChange={(value) => editBet({
                                        home: value,
                                        overtime_home: isCup && value === editingBet.away ? editingBet.overtime_home ?? value : undefined,
                                        overtime_away: isCup && value === editingBet.away ? editingBet.overtime_away ?? value : undefined,
                                        penalty_home: isCup && value === editingBet.away ? editingBet.penalty_home ?? 0 : undefined,
                                        penalty_away: isCup && value === editingBet.away ? editingBet.penalty_away ?? 0 : undefined,
                                    })} />
                                <NumberInput min={0} max={20} value={editingBet.away ?? 0}
                                    onChange={(value) => editBet({
                                        away: value,
                                        overtime_home: isCup && editingBet.home === value ? editingBet.overtime_home ?? value : undefined,
                                        overtime_away: isCup && editingBet.home === value ? editingBet.overtime_away ?? value : undefined,
                                        penalty_home: isCup && editingBet.home === value ? editingBet.penalty_home ?? 0 : undefined,
                                        penalty_away: isCup && editingBet.home === value ? editingBet.penalty_away ?? 0 : undefined,
                                    })} />
                            </div>
                        </div>

                        {isCup && editingBet.home === editingBet.away && (
                            <div className='match__bet__fieldset'>
                                <h4 className='match__bet__label'>Доп. время:</h4>
                                <div className='match__bet__fields'>
                                    <NumberInput min={0} max={20} value={editingBet.overtime_home ?? 0}
                                        onChange={(value) => editBet({
                                            overtime_home: value, overtime_away: editingBet.overtime_away ?? 0,
                                            penalty_home: isCup && value === editingBet.overtime_away ? editingBet.penalty_home ?? 0 : undefined,
                                            penalty_away: isCup && value === editingBet.overtime_away ? editingBet.penalty_away ?? 0 : undefined,
                                        })} />
                                    <NumberInput min={0} max={20} value={editingBet.overtime_away ?? 0}
                                        onChange={(value) => editBet({
                                            overtime_away: value, overtime_home: editingBet.overtime_home ?? 0,
                                            penalty_home: isCup && value === editingBet.overtime_home ? editingBet.penalty_home ?? 0 : undefined,
                                            penalty_away: isCup && value === editingBet.overtime_home ? editingBet.penalty_away ?? 0 : undefined,
                                        })} />
                                </div>
                            </div>
                        )}

                        {isCup && editingBet.home === editingBet.away && editingBet.overtime_home === editingBet.overtime_away && (
                            <div className='match__bet__fieldset'>
                                <h4 className='match__bet__label'>Пенальти:</h4>
                                <div className='match__bet__fields'>
                                    <NumberInput min={0} max={20} value={editingBet.penalty_home ?? 0}
                                        onChange={(value) => editBet({ penalty_home: value, penalty_away: editingBet.penalty_away ?? 0 })} />
                                    <NumberInput min={0} max={20} value={editingBet.penalty_away ?? 0}
                                        onChange={(value) => editBet({ penalty_away: value, penalty_home: editingBet.penalty_home ?? 0 })} />
                                </div>
                            </div>
                        )}

                        <div className='match__bet__fieldset'>
                            <h4 className='match__bet__label'>Ставка (баллы):</h4>
                            <div className='match__bet__stake'>
                                <NumberInput
                                    min={0}
                                    max={10}
                                    value={editingBet.stake ?? 0}
                                    onChange={(value) => editBet({ stake: Math.min(value ?? 0, 10) })}
                                />
                                {
                                    (place?.total_score ?? 0) === (place?.available_balance ?? 0) &&
                                    <button
                                        className='match__bet__stake__allin'
                                        onClick={() => editBet({stake: place?.total_score ?? 0})}
                                    >
                                        Ва-банк
                                    </button>
                                }
                            </div>
                            <div className='match__bet__balance'>
                                Доступно: {place?.available_balance ?? 0} / {place?.total_score ?? 0} баллов
                            </div>
                        </div>

                        <label className='match__bet__golden'>
                            <input type='checkbox' checked={editingBet.is_golden ?? false}
                                onChange={(e) => editBet({ is_golden: e.target.checked })} />
                            Золотой джокер (×2 баллы)
                        </label>

                        <div className='match__bet__buttons'>
                            <Button className='match__bet__buttons_button' onlyIcon iconLeft={IconClose} view='ghost' width='full' onClick={onCancel} />
                            <Button className='match__bet__buttons_button' onlyIcon iconLeft={IconShuffle} view='ghost' width='full'
                                onClick={() => editBet({ home: Math.floor(Math.random() * 5), away: Math.floor(Math.random() * 5) })} />
                            <Button className='match__bet__buttons_button' onlyIcon iconLeft={IconCheck} view='ghost' width='full'
                                loading={betLoading} onClick={() => onSave(match.id, bet)} />
                        </div>
                    </>
                )}

                {/* ОТОБРАЖЕНИЕ */}
                {!isEditing && bet && (
                    <>
                        <span className='match__score-label'>Прогноз</span>
                        <div className='match__bet__container'>
                            <div className='match__bet__score'>
                                <span>{bet.home}</span><span>-</span><span>{bet.away}</span>
                            </div>
                            {bet.overtime_home !== null && bet.overtime_away !== null && (
                                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: 4 }}>
                                    доп. {bet.overtime_home}–{bet.overtime_away}
                                    {bet.penalty_home !== null && `, пен. ${bet.penalty_home}–${bet.penalty_away}`}
                                </div>
                            )}
                            {canEdit && (
                                <button className='match__bet__buttons_button_edit'
                                    onClick={() => onEdit({
                                        match_id: match.id!,
                                        home: bet.home!, away: bet.away!,
                                        overtime_home: isCup ? bet.overtime_home ?? 0 : undefined,
                                        overtime_away: isCup ? bet.overtime_away ?? 0 : undefined,
                                        penalty_home: isCup ? bet.penalty_home ?? 0 : undefined,
                                        penalty_away: isCup ? bet.penalty_away ?? 0 : undefined,
                                        stake: bet.stake ?? 0,
                                        is_golden: bet.is_golden ?? false,
                                    })} />
                            )}
                        </div>
                            {bet.description && (
                                <div className='match__bet__description'>
                                    {bet.description}
                                </div>
                            )}

                        <div className='match__bet__summary'>
                            {match.status !== 'CLOSED' && bet.stake! > 0 && (
                                <span className={`match__bet__summary-item match__bet__summary-item--stake ${bet.is_all_in ? 'match__bet__summary-item--allin' : ''}`}>
                                    Ставка: {bet.stake} {bet.is_all_in ? '(ва-банк)' : ''}
                                </span>
                            )}
                            {bet.is_golden && (
                                <span className='match__bet__summary-item match__bet__summary-item--golden'>×2</span>
                            )}
                            {match.status === 'CLOSED' && (
                                <>
                                    {bet.classic_points! > 0 && (
                                        <span className={`match__bet__summary-item ${(bet.classic_points ?? 0) >= 0 ? '' : 'match__bet__summary-item--negative'}`}>
                                            Прогноз: {(bet.classic_points ?? 0) >= 0 ? '+' : ''}{bet.classic_points}
                                        </span>
                                    )}
                                    {bet.stake! > 0 && bet.stake_profit !== null && (
                                        <span className={`match__bet__summary-item ${(bet.stake_profit ?? 0) >= 0 ? '' : 'match__bet__summary-item--negative'}`}>
                                            Ставка: {(bet.stake_profit ?? 0) >= 0 ? '+' : ''}{bet.stake_profit}
                                        </span>
                                    )}
                                    <span className='match__bet__summary-item' style={{ borderColor: '#fff', color: '#fff', fontWeight: 700 }}>
                                        Итого: {(bet.classic_points ?? 0) + (bet.stake_profit ?? 0) > 0 ? '+' : ''}{(bet.classic_points ?? 0) + (bet.stake_profit ?? 0)}
                                    </span>
                                </>
                            )}
                        </div>
                    </>
                )}
                {/* КНОПКА ДОБАВИТЬ */}
                {!isEditing && !bet && canEdit && (
                    <button className='match__add-button'
                        onClick={() => onEdit({ match_id: match.id!, home: 0, away: 0, stake: 0, is_golden: false })}>
                        <IconAdd size='s' />Добавить прогноз
                    </button>
                )}
            </div>
        </div>
    );
};